import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, User } from '../../context/AppContext';
import { requestJson } from '../../api/http';
import { clearPrivateOfflineData, saveOfflineSnapshot } from '../../api/offlineSession';
import { readPostAuthReturn } from '../../auth/authReturn';
import { features } from '../../config/features';
import { PillButton } from '../ui';
import './StagingLogin.css';

/** A staging-only form for the existing password API, never an auth bypass. */
export default function StagingLogin() {
  const { dispatch } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  if (!features.stagingMode) return null;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const base = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
      const result = await requestJson<{ access_token: string; user: { id: string } }>(`${base}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!result.access_token || !result.user?.id) throw new Error('Invalid login response');
      // Verify the token with the normal authorization endpoint before exposing
      // authenticated UI or storing identity. Never put credentials in a URL.
      const user = await requestJson<User & { profile_picture_url?: string }>(`${base}/auth/me`, {
        headers: { Authorization: `Bearer ${result.access_token}` }, cache: 'no-store',
      });
      if (user.id !== result.user.id) throw new Error('Identity mismatch');
      await clearPrivateOfflineData();
      localStorage.setItem('auth_token', result.access_token);
      localStorage.setItem('user_id', user.id);
      const identity = { ...user, picture: user.profile_picture_url, isAuthenticated: true };
      await saveOfflineSnapshot('user', identity);
      setPassword('');
      dispatch({ type: 'SET_USER', payload: identity });
      navigate(readPostAuthReturn(), { replace: true });
    } catch {
      setPassword('');
      setError('Sign-in failed. Check your staging account details and connection, then try again.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  return <form className="staging-login" onSubmit={submit} aria-label="Staging account sign-in">
    <h3>Staging account</h3><p>Use your assigned test account to review this isolated environment.</p>
    <label>Email<input type="email" autoComplete="username" required value={email} disabled={busy} onChange={(event) => setEmail(event.target.value)} /></label>
    <label>Password<input type="password" autoComplete="current-password" required value={password} disabled={busy} onChange={(event) => setPassword(event.target.value)} /></label>
    {error && <p role="alert">{error}</p>}
    <PillButton variant="primary" type="submit" disabled={busy} busy={busy} busyLabel="Signing in…">Sign in to staging</PillButton>
  </form>;
}
